import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import AdminReservations from './AdminReservations';
import AdminReviews from './AdminReviews';
import AdminPortfolio from './AdminPortfolio';
import AdminContent from './AdminContent';
import AdminSettings from './AdminSettings';
import '../css/admin.css';

const TABS = [
  { id: 'reservations', icon: '📅', label: 'Reservations', short: 'Book' },
  { id: 'reviews',      icon: '⭐', label: 'Reviews',      short: 'Reviews' },
  { id: 'portfolio',    icon: '🖼️', label: 'Portfolio',    short: 'Work' },
  { id: 'content',      icon: '✏️', label: 'Page Content', short: 'Content' },
  { id: 'settings',     icon: '⚙️', label: 'Settings',     short: 'Settings' },
];

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('reservations');
  const [dark, setDark] = useState(() => localStorage.getItem('amko-admin-theme') === 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('amko-admin-theme', dark ? 'dark' : 'light');
  }, [dark]);
  const [reservations, setReservations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async u => {
      try {
        if (!u) { setUser(null); setLoading(false); return; }
        const token = await u.getIdTokenResult();
        if (token.claims.admin === true) {
          setUser(u);
        } else {
          await signOut(auth);
          setBlocked(true);
        }
        setLoading(false);
      } catch (err) {
        if (!err.message?.includes('INTERNAL ASSERTION FAILED')) {
          console.error('[AdminApp] auth error:', err.message);
        }
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const login = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      if (err.code === 'auth/cancelled-popup-request' || err.code === 'auth/popup-closed-by-user') return;
      console.error('[AdminApp] login error:', err.code, err.message);
    }
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const [rSnap, revSnap, pSnap] = await Promise.all([
        getDocs(query(collection(db, 'reservations'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'reviews'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'portfolio'), orderBy('createdAt', 'desc'))),
      ]);
      setReservations(rSnap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.()?.toISOString() })));
      setReviews(revSnap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.()?.toISOString() })));
      setPortfolio(pSnap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.()?.toISOString() })));
      setDataLoaded(true);
    };
    load();
  }, [user]);

  if (loading) return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #050d1f 0%, #0a1a3a 50%, #0d1424 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient orbs */}
      <div style={{ position:'absolute', width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle, #1A3A8F 0%, transparent 70%)', filter:'blur(80px)', opacity:0.3, top:'-10%', left:'-10%', animation:'adminOrbA 8s ease-in-out infinite' }} />
      <div style={{ position:'absolute', width:380, height:380, borderRadius:'50%', background:'radial-gradient(circle, #F5A623 0%, transparent 70%)', filter:'blur(80px)', opacity:0.2, bottom:'-5%', right:'-5%', animation:'adminOrbB 10s ease-in-out infinite' }} />
      <style>{`
        @keyframes adminOrbA { 0%,100%{transform:translate(0,0)}  50%{transform:translate(40px,-50px)} }
        @keyframes adminOrbB { 0%,100%{transform:translate(0,0)}  50%{transform:translate(-40px,30px)} }
        @keyframes adminSpin  { to { transform: rotate(360deg); } }
        @keyframes adminPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.92)} }
        @keyframes adminDots  { 0%,80%,100%{opacity:0.2;transform:translateY(0)} 40%{opacity:1;transform:translateY(-6px)} }
      `}</style>
      <div style={{ position:'relative', textAlign:'center', zIndex:1 }}>
        {/* Logo */}
        <img src="/logo.png" alt="AMKO" style={{ height:72, width:'auto', objectFit:'contain', marginBottom:'1.5rem', filter:'drop-shadow(0 0 24px rgba(26,58,143,0.7))', animation:'adminPulse 2.4s ease-in-out infinite' }} />
        {/* Brand */}
        <div style={{ fontSize:'2rem', fontWeight:900, letterSpacing:'0.08em', marginBottom:'0.3rem',
          background:'linear-gradient(135deg,#ffffff 30%,#93c5fd 100%)',
          WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
          AMKO
        </div>
        <div style={{ fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(255,255,255,0.35)', marginBottom:'2rem' }}>
          Admin Panel
        </div>
        {/* Spinner ring */}
        <div style={{ position:'relative', width:48, height:48, margin:'0 auto 1.5rem' }}>
          <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.06)' }} />
          <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'2px solid transparent', borderTopColor:'#F5A623', borderRightColor:'rgba(245,166,35,0.3)', animation:'adminSpin 0.9s linear infinite' }} />
        </div>
        {/* Bouncing dots */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
          {[0,150,300].map(delay => (
            <span key={delay} style={{ width:6, height:6, borderRadius:'50%', background:'rgba(255,255,255,0.35)', display:'inline-block', animation:`adminDots 1.2s ease-in-out ${delay}ms infinite` }} />
          ))}
        </div>
      </div>
    </div>
  );

  if (blocked) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
        <h1 style={{ color: '#ef4444', fontSize: '2rem', marginBottom: '0.5rem', fontFamily: 'monospace' }}>ACCESS BLOCKED</h1>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>This panel is restricted to authorized personnel only.</p>
        <p style={{ color: '#374151', fontSize: '0.75rem', marginBottom: '2rem', fontFamily: 'monospace', background: '#1a1a1a', padding: '0.75rem', borderRadius: 4, border: '1px solid #ef444433' }}>
          403 FORBIDDEN — Unauthorized access attempt has been logged.
        </p>
      </div>
    </div>
  );

  if (!user) return (
    <div className="admin-login-3d">
      <div className="login-bg-orbs">
        <span className="login-orb login-orb-1" />
        <span className="login-orb login-orb-2" />
        <span className="login-orb login-orb-3" />
      </div>
      <div className="login-grid-overlay" />
      <div className="login-scene">
        <div className="login-card-3d">
          <div className="login-card-badge">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5L12 1z"/></svg>
            Secure Access
          </div>
          <img src="/logo.png" alt="AMKO Logo" className="login-logo-img-3d" />
          <div className="login-brand-3d">AMKO</div>
          <div className="login-tagline-3d">Admin Panel</div>
          <div className="login-divider-3d" />
          <button className="google-btn-3d" onClick={login}>
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" width="18" />
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-panel">
      {/* Mobile top bar — logo + actions (sidebar is at bottom on mobile) */}
      <div className="admin-mobile-topbar">
        <div className="admin-mobile-brand">
          <img src="/logo.png" alt="AMKO" className="sidebar-logo-img" style={{ height:26 }} />
          <span>AMKO <span style={{ color:'#F5A623' }}>Admin</span></span>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button className="admin-theme-toggle" style={{ margin:0, padding:'0.35rem 0.65rem', fontSize:'0.75rem' }} onClick={() => setDark(d => !d)}>
            {dark ? '☀' : '🌙'}
          </button>
          <button className="nav-item logout" style={{ padding:'0.35rem 0.65rem', fontSize:'0.75rem', width:'auto', borderRadius:8 }} onClick={() => signOut(auth)}>
            🚪
          </button>
        </div>
      </div>

      <aside className="admin-sidebar">
        <div className="admin-logo">
          <img src="/logo.png" alt="AMKO Logo" className="sidebar-logo-img" />
          AMKO<div className="admin-label">Admin</div>
        </div>
        <nav>
          {TABS.map(t => (
            <button key={t.id} className={`nav-item ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              <span className="nav-icon">{t.icon}</span>
              <span className="nav-label-full">{t.label}</span>
              <span className="nav-label-short">{t.short}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button className="admin-theme-toggle" onClick={() => setDark(d => !d)}>
            {dark ? '☀' : '🌙'} {dark ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button className="nav-item logout" onClick={() => signOut(auth)}>🚪 Logout</button>
        </div>
      </aside>
      <main className="admin-main">
        <div className={`admin-main-editor ${tab === 'content' ? 'admin-main-editor-fullsite' : ''}`}>
          {tab === 'reservations' && <AdminReservations reservations={reservations} setReservations={setReservations} />}
          {tab === 'reviews'      && <AdminReviews reviews={reviews} setReviews={setReviews} dataLoaded={dataLoaded} />}
          {tab === 'portfolio'    && <AdminPortfolio items={portfolio} setItems={setPortfolio} />}
          {tab === 'settings'     && <AdminSettings />}
          {tab === 'content'      && <AdminContent />}
        </div>
      </main>
    </div>
  );
}
